import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  Stack,
  Typography,
} from '@mui/material';
import moment from 'moment';
import styles from '../../../../custom/style.module.css';
import classes from '../docFwb.module.css';
import {
  CustomButton,
  CustomDialogTitle,
  CustomDialogContent,
  CustomDialogAction,
  StyledCard,
} from '../../../../custom/components';
import { CloseIconBtn, DateTimeSecFormat } from '../../../../custom/constants';

const formatDate = (d) => (d && moment(d).isValid() ? moment(d).format(DateTimeSecFormat).toUpperCase() : d || '');

const FwbVersionPreviewModal = ({
  open = false,
  onClose,
  version = null,
  selectedVersion = 1,
  onSetMainVersion,
  isLoading = false,
}) => {
  if (!version) return null;

  const isMain = version.version === selectedVersion;
  const rawText = typeof version.fwb === 'string' ? version.fwb : '';
  const lines = rawText.split(/\r?\n/).filter((line) => line !== '');
  const creatorName = version.created_by?.name || 'System';
  const creatorRole = version.created_by?.role ? ` (${version.created_by.role})` : '';

  return (
    <Dialog
      aria-labelledby='version-preview-dialog-title'
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
    >
      <CustomDialogTitle id='version-preview-dialog-title' className={styles.modalTitle}>
        <Stack direction='row' alignItems='center'>
          <span>FWB Preview - {version.name || `Version ${version.version}`} </span>
          {isMain && (
            <Chip
              label='Main Version'
              size='small'
              className={classes.versionPreviewMainChip}
            />
          )}
        </Stack>
        <CloseIconBtn onClick={onClose} />
      </CustomDialogTitle>
      <CustomDialogContent className={styles.p3}>
        <StyledCard>
          {/* Metadata Banner */}
          <Box className={`${classes.versionMetadataGrid} ${version.updated_at ? classes.versionMetadataGridWithUpdate : ''}`}>
            <Box className={classes.versionMetadataCard}>
              <Typography variant='overline' className={classes.versionMetadataLabel}>
              Created by
              </Typography>
              <Typography variant='body2' className={classes.versionMetadataValue}>
                {creatorName}{creatorRole}
              </Typography>
              <Typography variant='caption' className={classes.versionMetadataDate}>
                {formatDate(version.created_at) || 'Initial'}
              </Typography>
            </Box>
            {version.updated_at && (
              <Box className={classes.versionMetadataCard}>
                <Typography variant='overline' className={classes.versionMetadataLabel}>
                Updated by
                </Typography>
                <Typography variant='body2' className={classes.versionMetadataValue}>
                  {version.updated_by?.name || 'User'}
                </Typography>
                <Typography variant='caption' className={classes.versionMetadataDate}>
                  {formatDate(version.updated_at)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* FWB Code Box with Line Numbers */}
          <Box className={classes.versionPreviewCode}>
            {/* Gutter */}
            <Box className={classes.versionPreviewGutter}>
              {lines.map((_, i) => (
                <div key={i + 1}>{i + 1}</div>
              ))}
            </Box>

            {/* Text Lines */}
            <Box className={classes.versionPreviewText}>
              {lines.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </Box>
          </Box>
        </StyledCard>
      </CustomDialogContent>

      <CustomDialogAction className={styles.fSpaceBtw}>
        <CustomButton variant='outlined' onClick={onClose} disabled={isLoading}>
          Cancel
        </CustomButton>

        {isMain ? (
          <CustomButton
            variant='contained'
            disabled
            color='secondary'
          >
            Current Main Version
          </CustomButton>
        ) : (
          <CustomButton
            variant='contained'
            color='primary'
            disabled={isLoading}
            onClick={() => {
              if (onSetMainVersion) {
                onSetMainVersion(version.version);
              }
            }}
          >
            Set as Main Version
          </CustomButton>
        )}
      </CustomDialogAction>
    </Dialog>
  );
};

export default FwbVersionPreviewModal;
